/**
 * StatCard — a single dashboard metric. Use `comingSoon` for metrics the
 * backend can't yet supply (renders an em dash + a "Coming soon" chip instead
 * of a fabricated number).
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface StatCardProps {
  label: ReactNode;
  value?: ReactNode;
  sub?: ReactNode;
  /** Colour of the sub line: `positive` = green, `muted` = faint (default). */
  tone?: 'positive' | 'muted';
  /** Render the placeholder state (no data available yet). */
  comingSoon?: boolean;
  className?: string;
}

export function StatCard({
  label,
  value,
  sub,
  tone = 'muted',
  comingSoon,
  className,
}: StatCardProps) {
  return (
    <div className={cn('rounded-xl border border-border p-6', className)}>
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-2 text-[30px] font-bold tracking-[-0.02em]">
        {comingSoon ? '—' : value}
      </div>
      {comingSoon ? (
        <span className="mt-1.5 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11.5px] font-medium text-faint">
          Coming soon
        </span>
      ) : (
        sub && (
          <div
            className={cn(
              'mt-1.5 text-[12.5px]',
              tone === 'positive' ? 'text-success' : 'text-faint',
            )}
          >
            {sub}
          </div>
        )
      )}
    </div>
  );
}
